import React, { useState, useEffect } from 'react';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { Box, Tooltip } from '@mui/material';
import { Typography } from '../Typography';
import { DurationInput } from './DurationInput';
import { StyledDurationBox } from './styledComponents';
import { convertDurationToSeconds, convertSecondsToDuration, convertSecondsToHoursAndMinutes } from './utils';

interface Duration {
  days: number;
  hours: number;
  minutes: number;
}
interface DurationFieldProps {
  t: (key: string, options?: Record<string, string | number>) => string;
  /*
   * The value is in seconds
   */
  value: number;
  onChange: (value: number) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  daysEnabled?: boolean;
  label?: string;
  debounceTime?: number;
  maxDays?: number;
  maxHours?: number;
  maxMinutes?: number;
  size?: 'small' | 'medium';
  readOnly?: boolean;
  readOnlyText?: React.ReactNode;
}

export const DurationPicker: React.FC<DurationFieldProps> = ({
  t,
  value,
  onChange,
  disabled = false,
  error = false,
  helperText = '',
  daysEnabled = false,
  label = '',
  debounceTime = 300,
  maxDays = 99,
  maxHours = 23,
  maxMinutes = 59,
  size = 'medium',
  readOnly = false,
  readOnlyText = undefined,
}) => {
  const [internalValue, setInternalValue] = useState<Duration>(
    daysEnabled ? convertSecondsToDuration(value) : convertSecondsToHoursAndMinutes(value),
  );
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setInternalValue(daysEnabled ? convertSecondsToDuration(value) : convertSecondsToHoursAndMinutes(value));
  }, [value, daysEnabled]);

  // Debounced onChange handler
  const handleChange = (newValue: { hours: number; minutes: number; days: number }) => {
    if (
      newValue.days === internalValue.days &&
      newValue.hours === internalValue.hours &&
      newValue.minutes === internalValue.minutes
    ) {
      return;
    }
    setInternalValue(newValue);

    if (debounceTime > 0) {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      const newTimeoutId = setTimeout(() => {
        onChange(convertDurationToSeconds(newValue));
      }, debounceTime);

      setTimeoutId(newTimeoutId);
    } else {
      // No debouncing, call onChange immediately
      onChange(convertDurationToSeconds(newValue));
    }
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  const daysLabel = t('common:days');
  const hoursLabel = t('common:hours');
  const minutesLabel = t('common:minutes');

  return (
    <Box>
      {label && (
        <Typography
          sx={{
            ml: '2px',
            mb: '4px',
            fontSize: '14px',
            fontWeight: 500,
            lineHeight: '20px',
            color: disabled ? '#667085' : '#344054',
          }}
          role="presentation"
        >
          {label}
        </Typography>
      )}

      <Tooltip title={readOnly ? readOnlyText : undefined} placement="top-end">
        <StyledDurationBox
          disabled={disabled}
          size={size}
          readOnly={readOnly}
          {...(error && { error: true })}
          role="group"
        >
          <HourglassEmptyIcon sx={{ mr: 1, color: '#667085' }} />
          {daysEnabled && (
            <Box sx={{ marginTop: '4px', display: 'flex', alignItems: 'center' }}>
              <DurationInput
                max={maxDays}
                placeholder="dd"
                disabled={disabled}
                value={internalValue.days}
                onChange={(val) => handleChange({ ...internalValue, days: val })}
                aria-label={daysLabel}
                name={daysLabel}
              />
              <Typography
                sx={{
                  userSelect: 'none',
                  mr: 1,
                  color: disabled ? '#667085' : undefined,
                }}
              >
                d
              </Typography>
            </Box>
          )}
          <Box sx={{ marginTop: '4px', display: 'flex', alignItems: 'center' }}>
            <DurationInput
              disabled={disabled}
              max={maxHours}
              placeholder="hh"
              value={internalValue.hours}
              onChange={(val) => handleChange({ ...internalValue, hours: val })}
              aria-label={hoursLabel}
              name={hoursLabel}
            />

            <Typography sx={{ mr: 1, color: disabled ? '#667085' : undefined }}>hr</Typography>

            <DurationInput
              disabled={disabled}
              max={maxMinutes}
              placeholder="mm"
              value={internalValue.minutes}
              onChange={(val) => handleChange({ ...internalValue, minutes: val })}
              aria-label={minutesLabel}
              name={minutesLabel}
            />

            <Typography sx={{ color: disabled ? '#667085' : undefined }}>min</Typography>
          </Box>
        </StyledDurationBox>
      </Tooltip>

      {helperText && (
        <Typography sx={{ color: 'error.main', fontSize: '12px', fontWeight: 400 }}>{helperText}</Typography>
      )}
    </Box>
  );
};
